import { useSearchParams } from "react-router-dom";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  BookOpenCheck,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Flag,
  ListTodo,
  LogIn,
  Percent,
  Target,
  Timer,
  XCircle,
} from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { adminApi } from "@/api/endpoints";
import { TrendChart } from "@/components/dashboard/trend-chart";
import { DeltaBadge } from "@/components/shared/delta-badge";
import { ErrorState } from "@/components/shared/error-state";
import { Panel } from "@/components/shared/panel";
import { StatTile } from "@/components/shared/stat-tile";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { currentLanguage } from "@/configs/i18n";
import { queryKeys } from "@/configs/query-keys";
import { readPeriod, shiftPeriod, splitMinutes, todayIn } from "@/lib/dashboard";
import { formatMonthYear, formatShortDay, percent } from "@/lib/format";
import { withParams } from "@/lib/url-params";
import { cn } from "@/lib/utils";
import { STATS_PERIODS, type StatsPeriod, type UserStats } from "@/types/api";

import { ActivityCalendar } from "./activity-calendar";

const PRIORITY_TONES = {
  HIGH: "bg-priority-high",
  MEDIUM: "bg-priority-medium",
  LOW: "bg-priority-low",
} as const;

/**
 * Foydalanuvchi statistikasi: kun (soatlar), hafta, oy (kunlar) yoki yil
 * (oylar). Davr va sana manzilda (`period`, `date`) — havola aynan shu
 * kesimni ochadi. Har ko'rsatkich oldingi teng davr bilan solishtiriladi.
 */
export function UserStatsTab({ userId, timeZone }: { userId: string; timeZone: string }) {
  const { t } = useTranslation();
  const lang = currentLanguage();
  const [params, setParams] = useSearchParams();
  const today = todayIn(timeZone);
  const { period, date } = readPeriod(params, today);
  const filters = { period, date };
  const stats = useQuery({
    queryKey: queryKeys.userStats(userId, filters),
    queryFn: () => adminApi.userStats(userId, filters),
    placeholderData: keepPreviousData,
  });

  const go = (next: { period?: StatsPeriod; date?: string }) =>
    setParams(
      prev => withParams(prev, { period: next.period ?? period, date: next.date ?? date }),
      {
        replace: false,
      },
    );

  const next = shiftPeriod(period, date, 1);
  const data = stats.data;
  const title = data ? periodTitle(data, lang) : "";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          role="tablist"
          aria-label={t("userStats.period")}
          className="bg-card flex rounded-2xl border p-1 shadow-xs"
        >
          {STATS_PERIODS.map(item => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={item === period}
              onClick={() => go({ period: item })}
              className={cn(
                "relative h-8 rounded-xl px-3.5 text-sm font-medium transition-colors",
                item === period
                  ? "text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {item === period ? (
                <motion.span
                  layoutId="stats-period"
                  className="bg-primary shadow-primary/25 absolute inset-0 rounded-xl shadow-sm"
                  transition={{ type: "spring", stiffness: 420, damping: 32 }}
                />
              ) : null}
              <span className="relative">{t(`userStats.periods.${item}`)}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("userStats.prev")}
            onClick={() => go({ date: shiftPeriod(period, date, -1) })}
          >
            <ChevronLeft />
          </Button>
          <span className="min-w-40 text-center text-sm font-semibold" aria-live="polite">
            {title}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("userStats.next")}
            disabled={next > today}
            onClick={() => go({ date: next })}
          >
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="ml-1 rounded-xl"
            disabled={date === today}
            onClick={() => go({ date: today })}
          >
            {t("userStats.today")}
          </Button>
        </div>
      </div>

      {stats.isPending ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : stats.isError ? (
        <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />
      ) : (
        <StatsBody
          data={stats.data}
          today={today}
          onPickDay={day => go({ period: "day", date: day })}
        />
      )}
    </div>
  );
}

function periodTitle(data: UserStats, lang: string): string {
  if (data.period === "day") return `${formatShortDay(data.from, lang)} ${data.from.slice(0, 4)}`;
  if (data.period === "week")
    return `${formatShortDay(data.from, lang)} — ${formatShortDay(data.to, lang)}`;
  if (data.period === "month") return formatMonthYear(data.from, lang);
  return data.from.slice(0, 4);
}

function StatsBody({
  data,
  today,
  onPickDay,
}: {
  data: UserStats;
  today: string;
  onPickDay: (date: string) => void;
}) {
  const { t } = useTranslation();
  const lang = currentLanguage();
  const { totals, previous } = data;
  const focus = splitMinutes(totals.focusMinutes);
  const weekdays = t("dashboard.weekdays").split(",");

  const label = (key: string, index: number) => {
    if (data.period === "day") return key;
    if (data.period === "week") return weekdays[index] ?? key;
    if (data.period === "month") return String(Number(key.slice(8, 10)));
    return formatMonthYear(key, lang).split(" ")[0]?.slice(0, 3) ?? key;
  };
  const byHour = data.period === "day";

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatTile
          index={0}
          icon={ListTodo}
          tone="sky"
          label={t("userStats.planned")}
          value={totals.planned}
          aside={<DeltaBadge current={totals.planned} previous={previous.planned} />}
        />
        <StatTile
          index={1}
          icon={CheckCircle2}
          tone="mint"
          label={t("userStats.completed")}
          value={totals.completed}
          aside={<DeltaBadge current={totals.completed} previous={previous.completed} />}
        />
        <StatTile
          index={2}
          icon={Percent}
          tone={totals.completionRate >= 60 ? "mint" : "amber"}
          label={t("userStats.rate")}
          value={`${totals.completionRate}%`}
          aside={
            <DeltaBadge current={totals.completionRate} previous={previous.completionRate} points />
          }
        />
        <StatTile
          index={3}
          icon={Timer}
          tone="violet"
          label={t("userStats.focus")}
          value={t(focus.hours >= 100 ? "dashboard.hoursOnly" : "dashboard.hoursMinutes", focus)}
          aside={<DeltaBadge current={totals.focusMinutes} previous={previous.focusMinutes} />}
        />
        <StatTile
          index={4}
          icon={CalendarClock}
          tone="amber"
          label={t("userStats.postponed")}
          value={totals.postponed}
          aside={<DeltaBadge current={totals.postponed} previous={previous.postponed} invert />}
        />
        <StatTile
          index={5}
          icon={XCircle}
          tone="coral"
          label={t("userStats.cancelled")}
          value={totals.cancelled}
          aside={<DeltaBadge current={totals.cancelled} previous={previous.cancelled} invert />}
        />
        <StatTile
          index={6}
          icon={CalendarDays}
          tone="mint"
          label={t("userStats.activeDays")}
          value={totals.activeDays}
          aside={<DeltaBadge current={totals.activeDays} previous={previous.activeDays} />}
        />
        <StatTile
          index={7}
          icon={LogIn}
          tone="sky"
          label={t("userStats.logins")}
          value={totals.logins}
          aside={<DeltaBadge current={totals.logins} previous={previous.logins} />}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          icon={BarChart3}
          tone="bg-tile-coral text-tile-coral-strong"
          title={byHour ? t("userStats.chartHours") : t("userStats.chart")}
          description={byHour ? t("userStats.legendHours") : t("userStats.legend")}
          className="lg:col-span-2"
        >
          <TrendChart
            label={t("userStats.chart")}
            points={data.buckets.map((bucket, index) => ({
              key: bucket.key,
              label: label(bucket.key, index),
              value: bucket.completed,
              base: byHour ? bucket.created : bucket.planned,
            }))}
          />
        </Panel>
        <Panel
          icon={Flag}
          tone="bg-tile-amber text-tile-amber-strong"
          title={t("userStats.priority")}
          delay={0.06}
        >
          <ul className="space-y-3">
            {data.byPriority.map((row, index) => (
              <li key={row.priority} className="space-y-1">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className={cn("size-2.5 rounded-full", PRIORITY_TONES[row.priority])} />
                    {t(`userStats.priorities.${row.priority}`)}
                  </span>
                  <span className="tabular-nums">
                    <b>{row.completed}</b>
                    <span className="text-muted-foreground">/{row.planned}</span>
                  </span>
                </div>
                <div className="bg-muted h-2 overflow-hidden rounded-full">
                  <motion.div
                    className={cn("h-full rounded-full", PRIORITY_TONES[row.priority])}
                    initial={{ width: 0 }}
                    animate={{ width: `${percent(row.completed, row.planned)}%` }}
                    transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
                  />
                </div>
              </li>
            ))}
          </ul>
          <dl className="grid grid-cols-3 gap-2 border-t pt-4 text-center">
            {(
              [
                [Target, t("userStats.goalsCreated"), totals.goalsCreated],
                [Flag, t("userStats.goalsCompleted"), totals.goalsCompleted],
                [BookOpenCheck, t("userStats.reviews"), totals.reviews],
              ] as const
            ).map(([Icon, text, value]) => (
              <div key={text}>
                <dd className="text-lg font-semibold tabular-nums">{value}</dd>
                <dt className="text-muted-foreground flex items-center justify-center gap-1 text-[11px]">
                  <Icon className="size-3" aria-hidden />
                  {text}
                </dt>
              </div>
            ))}
          </dl>
        </Panel>
      </div>

      <Panel
        icon={CalendarDays}
        tone="bg-tile-mint text-tile-mint-strong"
        title={t("userStats.calendar")}
        description={t("userStats.calendarHint")}
        delay={0.1}
      >
        <ActivityCalendar entries={data.calendar} today={today} onPick={onPickDay} />
      </Panel>
    </>
  );
}
